import { Field, useFormContext } from '@oscar/form';
import { Col, Input, InputNumber, Row } from '@oscar/theme';
import { DatePickerPopulate, FileUploaded, TextField } from 'common/components';
import { FORMAT_DATE } from 'common/constants';
import { APIS } from 'common/constants/endpoints';
import { getDateCorrect } from 'features/insulation/utils';
import React, { useMemo } from 'react';
import { StatusInstance } from 'views/pages/create-instrulation/hooks/useStatus';
import { getStage } from '../utils';
import ListCheckList from './ListCheckList';

const { TextArea } = Input;

function TextRemark(props: { value?: any; label: string }) {
  const { value, label } = props;

  return (
    <div className="text-field">
      <div className="text-label">{label}</div>
      <div className="text-description text-remark">{value}</div>
    </div>
  );
}

interface Props {
  statusRequest?: StatusInstance;
}

const isReadOnly = (statusRequest?: StatusInstance) => {
  return statusRequest?.state === 'VIEW';
};

function PaintingExecution({ statusRequest }: Props) {
  const [formContext] = useFormContext();
  const submitQAQCsTime =
    formContext.selectors?.getInitalValue()?.submitQAQCsTime;

  const stage = useMemo(() => {
    const total = submitQAQCsTime?.needToSubmittedTime;
    const currentStage = submitQAQCsTime?.submittedTime;
    if (total > 0) {
      return `${getStage(currentStage)}`;
    }
    return '';
  }, [submitQAQCsTime]);

  function disabledDate(current: any) {
    const requestedDate =
      formContext.selectors?.getInitalValue()?.requestedDate;
    return current && current.isBefore(getDateCorrect(requestedDate), 'day');
  }

  return (
    <>
      <ListCheckList statusRequest={statusRequest} />
      <div className="surface-prep">
        {statusRequest?.state === 'EDIT' &&
        submitQAQCsTime?.submittedTime !==
          submitQAQCsTime?.needToSubmittedTime ? (
          <>
            <Row gutter={[15, 15]}>
              <Col span={24}>
                <p className="label-normal">
                  <b>Application Product</b>
                </p>
                {stage && <div>{stage}</div>}
              </Col>
            </Row>
            <Row gutter={[15, 15]}>
              <Col span={8}>
                <p className="label-normal">Paint Product</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'paintProduct',
                  ]}
                >
                  <Input placeholder="Paint Product" maxLength={40} />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Paint Batch Number Part A</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'paintBatchNumberPathA',
                  ]}
                >
                  <Input
                    placeholder="Paint Batch Number Part A"
                    maxLength={40}
                  />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Paint Batch Number Part B</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'paintBatchNumberPathB',
                  ]}
                >
                  <Input
                    placeholder="Paint Batch Number Part B"
                    maxLength={40}
                  />
                </Field>
              </Col>
            </Row>
            <Row gutter={[15, 15]}>
              <Col span={8}>
                <p className="label-normal">Equipment Used</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'equipmentUsed',
                  ]}
                >
                  <Input placeholder="Equipment Used" maxLength={40} />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Average WFT (µm)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'averageWFT',
                  ]}
                >
                  <InputNumber
                    placeholder="Average WFT (µm)"
                    min="0"
                    decimalSeparator="."
                    precision={2}
                  />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Average DFT (µm)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'averageDFT',
                  ]}
                >
                  <InputNumber
                    placeholder="Average DFT (µm)"
                    min="0"
                    decimalSeparator="."
                    precision={2}
                  />
                </Field>
              </Col>
            </Row>
            <Row gutter={[15, 15]}>
              <Col span={8}>
                <p className="label-normal">Weather</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'weather',
                  ]}
                >
                  <Input placeholder="Weather" maxLength={40} />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Relative Humidity (%)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'relativeHumidity',
                  ]}
                >
                  <InputNumber
                    placeholder="Relative Humidity"
                    min="0"
                    decimalSeparator="."
                    precision={2}
                  />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Dew Point (°C)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'dewPoint',
                  ]}
                >
                  <InputNumber
                    placeholder="Dew Point"
                    min="0"
                    decimalSeparator="."
                    precision={2}
                  />
                </Field>
              </Col>
            </Row>
            <Row gutter={[15, 15]}>
              <Col span={8}>
                <p className="label-normal">Dry Bulb Temperature (°C)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'dryBulbTemperature',
                  ]}
                >
                  <InputNumber
                    placeholder="Dry Bulb Temperature"
                    min="0"
                    decimalSeparator="."
                    precision={2}
                  />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Wet Bulb Temperature (°C)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'wetBulbTemperature',
                  ]}
                >
                  <InputNumber
                    placeholder="Wet Bulb Temperature"
                    min="0"
                    decimalSeparator="."
                    precision={2}
                  />
                </Field>
              </Col>
              <Col span={8}>
                <p className="label-normal">Substrate Temperature (°C)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'substrateTemperature',
                  ]}
                >
                  <InputNumber
                    placeholder="Substrate Temperature"
                    min="0"
                    decimalSeparator="."
                    precision={2}
                  />
                </Field>
              </Col>
            </Row>
            <Row gutter={[15, 15]}>
              <Col span={24}>
                <FileUploaded
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'attachments',
                  ]}
                  title="Attachment"
                  request={{
                    name: 'attachment',
                    url: APIS.UPLOAD_FILE_TO_AZURE,
                    method: 'POST',
                  }}
                />
              </Col>
            </Row>
            <Row gutter={[15, 15]} style={{ marginTop: 24 }}>
              <Col span={12}>
                <p className="label-normal">Work Leader Name ({stage})</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'workLeaderName',
                  ]}
                >
                  <Input maxLength={100} placeholder={`Work Leader Name`} />
                </Field>
              </Col>
              <Col span={12}>
                <p className="label-normal">Painting Completion</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'paintingCompletion',
                  ]}
                >
                  <DatePickerPopulate
                    className="date-picker"
                    disabledDate={disabledDate}
                    inputReadOnly={true}
                    format={FORMAT_DATE.SHORT_MONTH_DATE}
                  />
                </Field>
              </Col>
            </Row>
            <Row gutter={[15, 15]}>
              <Col span={12}>
                <p className="label-normal">Inspected By (Contractor CI)</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'inspectedByName',
                  ]}
                >
                  <Input
                    maxLength={100}
                    placeholder={`Inspected By (Contractor CI)`}
                  />
                </Field>
              </Col>
              <Col span={12}>
                <p className="label-normal">Inspection Date & Time</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'inspectionDateAndTime',
                  ]}
                >
                  <DatePickerPopulate
                    className="date-picker"
                    disabledDate={disabledDate}
                    inputReadOnly={true}
                    format={FORMAT_DATE.SHORT_MONTH_DATE}
                  />
                </Field>
              </Col>
            </Row>
            <Row gutter={[15, 15]}>
              <Col span={24}>
                <p className="label-normal">Remarks</p>
                <Field
                  name={[
                    'qaqcInfo',
                    'listQAQCDetail',
                    submitQAQCsTime?.submittedTime,
                    'paintingConditionExecution',
                    'remarks',
                  ]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Maximum 250 characters"
                    maxLength={250}
                  />
                </Field>
              </Col>
            </Row>
            {statusRequest?.status?.includes('PENDING_QAQC') && (
              <Row gutter={[15, 15]}>
                <Col span={12}>
                  <p className="label-normal">Witnessed by (MLNG CI)</p>
                  <Field
                    name={[
                      'qaqcInfo',
                      'listQAQCDetail',
                      submitQAQCsTime?.submittedTime,
                      'paintingConditionExecution',
                      'witnessByName',
                    ]}
                  >
                    <Input
                      maxLength={100}
                      placeholder={`Witnessed by (MLNG CI)`}
                    />
                  </Field>
                </Col>
                <Col span={12}>
                  <p className="label-normal">Date</p>
                  <Field
                    name={[
                      'qaqcInfo',
                      'listQAQCDetail',
                      submitQAQCsTime?.submittedTime,
                      'paintingConditionExecution',
                      'date',
                    ]}
                  >
                    <DatePickerPopulate
                      className="date-picker"
                      disabledDate={disabledDate}
                      inputReadOnly={true}
                      format={FORMAT_DATE.SHORT_MONTH_DATE}
                    />
                  </Field>
                </Col>
              </Row>
            )}
          </>
        ) : (
          <>
            {submitQAQCsTime?.submittedTime !==
              submitQAQCsTime?.needToSubmittedTime && (
              <>
                <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
                  <Col span={24}>
                    <p className="label-normal">
                      <b>Application Product</b>
                    </p>
                    {stage && <div>{stage}</div>}
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'paintProduct',
                      ]}
                    >
                      <TextField label="Paint Product" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'paintBatchNumberPathA',
                      ]}
                    >
                      <TextField label="Paint Batch Number Part A" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'paintBatchNumberPathB',
                      ]}
                    >
                      <TextField label="Paint Batch Number Part B" />
                    </Field>
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'equipmentUsed',
                      ]}
                    >
                      <TextField label="Equipment Used" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'averageWFT',
                      ]}
                    >
                      <TextField label="Average WFT (µm)" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'averageDFT',
                      ]}
                    >
                      <TextField label="Average DFT (µm)" />
                    </Field>
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'weather',
                      ]}
                    >
                      <TextField label="Weather" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'relativeHumidity',
                      ]}
                    >
                      <TextField label="Relative Humidity (%)" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'dewPoint',
                      ]}
                    >
                      <TextField label="Dew Point (°C)" />
                    </Field>
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'dryBulbTemperature',
                      ]}
                    >
                      <TextField label="Dry Bulb Temperature (°C)" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'wetBulbTemperature',
                      ]}
                    >
                      <TextField label="Wet Bulb Temperature (°C)" />
                    </Field>
                  </Col>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'substrateTemperature',
                      ]}
                    >
                      <TextField label="Substrate Temperature (°C)" />
                    </Field>
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={24}>
                    <FileUploaded
                      readOnly={isReadOnly(statusRequest)}
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'attachments',
                      ]}
                      title="Attachment"
                      request={{
                        name: 'attachment',
                        url: APIS.UPLOAD_FILE_TO_AZURE,
                        method: 'POST',
                      }}
                    />
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'workLeaderName',
                      ]}
                    >
                      <TextField label={`Work Leader Name (${stage})`} />
                    </Field>
                  </Col>
                  <Col span={16}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'paintingCompletion',
                      ]}
                    >
                      <TextField
                        label="Painting Completion"
                        isDate
                        formartDate={FORMAT_DATE.SHORT_MONTH_DATE}
                      />
                    </Field>
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={8}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'inspectedByName',
                      ]}
                    >
                      <TextField label="Inspected By (Contractor CI)" />
                    </Field>
                  </Col>
                  <Col span={16}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'inspectionDateAndTime',
                      ]}
                    >
                      <TextField
                        label="Inspection Date & Time"
                        isDate
                        formartDate={FORMAT_DATE.FULL_DATETIME_HOUR}
                      />
                    </Field>
                  </Col>
                </Row>
                <Row gutter={[20, 20]}>
                  <Col span={24}>
                    <Field
                      name={[
                        'qaqcInfo',
                        'listQAQCDetail',
                        submitQAQCsTime?.submittedTime,
                        'paintingConditionExecution',
                        'remarks',
                      ]}
                    >
                      <TextRemark label="Remarks" />
                    </Field>
                  </Col>
                </Row>
                {/* {statusRequest?.status?.includes('PENDING_QAQC') && (
                  <Row gutter={[20, 20]}>
                    <Col span={8}>
                      <Field
                        name={[
                          'qaqcInfo',
                          'listQAQCDetail',
                          submitQAQCsTime?.submittedTime,
                          'paintingConditionExecution',
                          'witnessByName',
                        ]}
                      >
                        <TextField label="Witness by (MLNG CI)" />
                      </Field>
                    </Col>
                    <Col span={16}>
                      <Field
                        name={[
                          'qaqcInfo',
                          'listQAQCDetail',
                          submitQAQCsTime?.submittedTime,
                          'paintingConditionExecution',
                          'date',
                        ]}
                      >
                        <TextField label="Date" isDate />
                      </Field>
                    </Col>
                  </Row>
                )} */}
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default PaintingExecution;
